import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { useAuth } from '../../api/AuthContext';
import ReactModal from 'react-modal';
import PendingChargesSection from './PendingChargesSection';
import HistorialPagosSection from './HistorialPagosSection';
import styles from '../../styles/GeneracionCargosManualesStyles/GeneracionCargosManuales.module.css';
import {
  getConceptosActivos,
  getCiclosEscolares,
  getAdeudosPendientes,
  crearCargosManuales,
  getCategoriaByNombre,
  calcularMontoNeto,
  validarCargo,
  formatearMonto,
  enviarReciboCargosManuales,
  getConfigRecibos,
  getBecaEstudiante,
} from '../../api/cargosManuales';
import { crearPagoYAplicar, getDetalleEstudiantePagos } from '../../api/aplicacionPagos';
import { generateReciboPDF } from '../../utils/generateReciboPDF';
import jsPDF from 'jspdf';
import axios from 'axios';

// Diccionario para mostrar el label de la forma de pago
const FORMA_PAGO_LABELS = {
  'PAGO_EN_LINEA': 'Pago en línea',
  'TARJETA_DEBITO': 'Tarjeta de débito',
  'TARJETA_CREDITO': 'Tarjeta de crédito',
  'EFECTIVO': 'Efectivo',
  'TRANSFERENCIA': 'Transferencia bancaria',
  'DEPOSITO': 'Depósito bancario',
  'CONTADO': 'Contado',
  'PARCIALIDADES': 'Parcialidades',
};

// ErrorBoundary for catching render errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      let errorMsg = this.state.error;
      if (typeof errorMsg === 'object' && errorMsg !== null) {
        // Si es un objeto React, mostrar solo el nombre del tipo
        if (errorMsg.$$typeof && errorMsg.type) {
          return <div style={{color: 'red', padding: 24}}><b>Render error:</b> [React Element: {(errorMsg.type.name || 'Unknown')}]</div>;
        } else {
          try {
            errorMsg = JSON.stringify(errorMsg);
          } catch {
            errorMsg = '[object Object]';
          }
        }
      }
      return <div style={{color: 'red', padding: 24}}><b>Render error:</b> {errorMsg}</div>;
    }
    return this.props.children;
  }
}

function ChargeForm({ student, campusId, onBack, recargoCalculado = 0, recargosById = {} }) {
    // LOG 1: props.recargosById al renderizar
    useEffect(() => {
      console.log('[DEBUG] props.recargosById al renderizar ChargeForm:', recargosById);
    }, [recargosById]);
  // Ref para guardar la selección anterior de cargos
  const prevSelectedCargosRef = useRef([]);
  // ...eliminado previsualización PDF...
  const { user } = useAuth();
  const _campusRaw = ((student?.campus || student?.campus_nombre || student?.nombre_campus || '') + '');
  const campusNombreNormalized = typeof _campusRaw === 'string' && _campusRaw.normalize
    ? _campusRaw.normalize('NFD').replace(/\p{Diacritic}/gu, '')
    : _campusRaw;
  const campusNombreUpper = (campusNombreNormalized + '').toString().toUpperCase();
  const campusEsTeran = campusNombreUpper.includes('TERAN');
  const campusEsMonterrey = campusNombreUpper.includes('MONTERREY');
  const [showPreview, setShowPreview] = useState(false);
  const [conceptos, setConceptos] = useState([]);
  const [ciclos, setCiclos] = useState([]);
  const [cicloSeleccionado, setCicloSeleccionado] = useState('');
  const [cicloFiltroConceptos, setCicloFiltroConceptos] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingChargesKey, setPendingChargesKey] = useState(0);
  const [historialPagosKey, setHistorialPagosKey] = useState(0);
  const [recargoMap, setRecargoMap] = useState({});
  const [reciboCcEmails, setReciboCcEmails] = useState([]);
  const [reciboCcLoaded, setReciboCcLoaded] = useState(false);
  
  // Estado para cargos seleccionados desde PendingChargesSection
  const [selectedCargos, setSelectedCargos] = useState([]);
  // Estado con información detallada de los cargos seleccionados (para el recibo)
  const [selectedChargesForReceipt, setSelectedChargesForReceipt] = useState([]);
  
  // Estado para la beca activa del estudiante
  const [becaEstudiante, setBecaEstudiante] = useState(null);
  
  // Estado para datos del pago (el monto se toma directamente de la tabla de cargos o se ingresa manualmente)
  // Función para obtener la fecha actual en formato YYYY-MM-DD usando zona horaria de México
  const getFechaActual = () => {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });
  };
  
  // Función para obtener hora actual en formato HH:MM usando zona horaria de México
  const getHoraActual = () => {
    const now = new Date();
    const hora = now.toLocaleString('es-MX', { 
      timeZone: 'America/Mexico_City', 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
    return hora;
  };

  const [paymentData, setPaymentData] = useState({
    forma_pago: '',
    referencia_bancaria: '',
    fecha_pago: getFechaActual(),
    observaciones: '',
    monto_directo: ''
  });
  
  // Estado para controlar carga de pago
  const [applyingPayment, setApplyingPayment] = useState(false);
  
  // Handler para recibir la selección de cargos desde PendingChargesSection
  // Guardar la última lista de adeudos para reconstruir el recibo si cambia la fecha
  const lastAdeudosRef = useRef([]);
  // fechaPagoOverride permite forzar la fecha de pago a usar (por ejemplo, cuando el usuario la acaba de cambiar)
  const handleSelectionChange = async (selection, adeudosList = [], fechaPagoOverride = null) => {
    // Usar ref para comparar la selección anterior
    const prevSelected = prevSelectedCargosRef.current || [];
    if (Array.isArray(selection) && selection.length < prevSelected.length) {
      setPaymentData({
        forma_pago: '',
        referencia_bancaria: '',
        fecha_pago: getFechaActual(),
        observaciones: '',
        monto_directo: ''
      });
    }
    setSelectedCargos(selection || []);
    prevSelectedCargosRef.current = selection || [];
    // Guardar la última lista de adeudos
    if (Array.isArray(adeudosList) && adeudosList.length > 0) {
      lastAdeudosRef.current = adeudosList;
    }
    // Construir una lista de cargos con información suficiente para el PDF
    const adeudosToUse = Array.isArray(adeudosList) && adeudosList.length > 0 ? adeudosList : lastAdeudosRef.current;
    if (Array.isArray(selection) && selection.length > 0 && Array.isArray(adeudosToUse) && adeudosToUse.length > 0) {
      const cargosDetallados = selection.map((sel) => {
        const adeudo = adeudosToUse.find((a) => a.id === sel.id_cargo) || {};
        const recargo = recargoMap[sel.id_cargo] || 0;
        const monto_aplicado = sel.monto_aplicado || 0;
        return {
          id_cargo: sel.id_cargo,
          monto: monto_aplicado,
          cantidad: 1,
          motivo: adeudo.concepto || adeudo.descripcion || '',
          id_concepto: adeudo.codigo || '',
          monto_recargo: recargo,
          importe: monto_aplicado + recargo,
        };
      });
      setSelectedChargesForReceipt(cargosDetallados);
    } else {
      setSelectedChargesForReceipt([]);
    }
    // Recalcular recargo para cargos seleccionados, usando las mismas reglas
    // que PendingChargesSection: solo cuando exista una categoría de recargo
    // definida en la tabla de categorías (window.__lastCategoriasRecargo).
    if (Array.isArray(selection) && selection.length > 0) {
      const recargosLocal = {};
      // Obtener categorías de recargo calculadas por PendingChargesSection
      let categoriasRecargo = [];
      if (typeof window !== 'undefined' && Array.isArray(window.__lastCategoriasRecargo)) {
        categoriasRecargo = window.__lastCategoriasRecargo;
      }

      // Helper local para normalizar texto (misma lógica que en PendingChargesSection)
      const normalizeText = (s) => {
        if (!s && s !== 0) return '';
        return String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '').replace(/\s+/g, ' ').trim().toUpperCase();
      };

      // Helper local para emparejar adeudo con categoría de recargo
      const matchCategoriaRecargoLocal = (adeudo, cat) => {
        if (!adeudo || !cat) return false;
        
        // Validación 1: Solo aplicar recargo a conceptos PARCIALIDADES
        const categoriaConcepto = normalizeText(
          adeudo.categoria_concepto || adeudo.categoria || adeudo.nombre_categoria || ''
        );
        if (categoriaConcepto && !categoriaConcepto.includes('PARCIALIDADES')) {
          return false;
        }
        
        const adeudoCodigo = normalizeText(
          adeudo.codigo || adeudo.codigo_concepto || adeudo.clave || adeudo.code || ''
        );
        const catCodigo = normalizeText(
          cat.codigo || cat.codigo_concepto || cat.clave || cat.code || ''
        );
        if (adeudoCodigo && catCodigo) return adeudoCodigo === catCodigo;
        const adeudoCat = normalizeText(
          adeudo.categoria || adeudo.nombre_categoria || adeudo.subcategoria || ''
        );
        const catCat = normalizeText(
          cat.categoria || cat.nombre_categoria || cat.subcategoria || ''
        );
        return !!(adeudoCat && catCat && adeudoCat === catCat);
      };

      const { calcularRecargoCategoria } = require('../../api/recargos');

      for (let i = 0; i < selection.length; i++) {
        let cargo = selection[i];
        // Buscar el adeudo correspondiente en la lista (o en la última lista conocida)
        let adeudoBaseList = Array.isArray(adeudosList)
          ? adeudosList
          : (Array.isArray(lastAdeudosRef.current) && lastAdeudosRef.current.length > 0
            ? lastAdeudosRef.current
            : (typeof window !== 'undefined' && Array.isArray(window.__lastAdeudosPendientes)
              ? window.__lastAdeudosPendientes
              : []));

        let adeudo = Array.isArray(adeudoBaseList)
          ? adeudoBaseList.find(a => a.id === cargo.id_cargo)
          : null;

        if (!adeudo) {
          recargosLocal[cargo.id_cargo] = 0;
          continue;
        }

        const fechaVencimiento = adeudo.fecha_vencimiento || cargo.fecha_vencimiento || cargo.fechaVencimiento || new Date();
        const fechaPago = fechaPagoOverride || (paymentData && paymentData.fecha_pago ? paymentData.fecha_pago : new Date());

        // Si el campus es Teran, nunca aplicar recargo
        if (campusEsTeran) {
          recargosLocal[cargo.id_cargo] = 0;
          continue;
        }

        if (!Array.isArray(categoriasRecargo) || categoriasRecargo.length === 0 || typeof calcularRecargoCategoria !== 'function') {
          recargosLocal[cargo.id_cargo] = 0;
          continue;
        }

        const cat = categoriasRecargo.find(c => matchCategoriaRecargoLocal(adeudo, c));
        if (!cat) {
          // Adeudo sin categoría de recargo configurada -> recargo 0
          recargosLocal[cargo.id_cargo] = 0;
          continue;
        }

        try {
          const recargo = calcularRecargoCategoria(cat, fechaPago, fechaVencimiento) || 0;
          recargosLocal[cargo.id_cargo] = recargo;
        } catch (errCalc) {
          console.error('Error calculando recargo en selección:', errCalc);
          recargosLocal[cargo.id_cargo] = 0;
        }
      }

      setRecargoMap(recargosLocal);
    } else {
      setRecargoMap({});
    }
  };
  
  // Handler para cambios en el formulario de pago
  const handlePaymentDataChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
    // Si cambia la fecha de pago y hay cargos seleccionados, recalcular recargos
    if (field === 'fecha_pago' && selectedCargos.length > 0) {
      // Obtener la lista de adeudos más reciente para asegurar que el lookup funcione
      const adeudosActuales =
        (typeof window !== 'undefined' && Array.isArray(window.__lastAdeudosPendientes) && window.__lastAdeudosPendientes.length > 0)
          ? window.__lastAdeudosPendientes
          : lastAdeudosRef.current;
      handleSelectionChange(selectedCargos, adeudosActuales, value);
    }
  };
  

  // Obtener el recargo mostrado en el pie de página de PendingChargesSection
  const getDisplayRecargos = () => {
    // window.__lastDisplayRecargos es actualizado por PendingChargesSection
    if (typeof window !== 'undefined' && typeof window.__lastDisplayRecargos === 'number') {
      return window.__lastDisplayRecargos;
    }
    // Fallback: suma local
    return selectedCargos.reduce((sum, item) => sum + (recargoMap[item.id_cargo] || 0), 0);
  };

  const [totalSeleccionado, setTotalSeleccionado] = useState(0);
  const [totalRecargo, setTotalRecargo] = useState(0);
  const [totalConRecargo, setTotalConRecargo] = useState(0);

  useEffect(() => {
    // Sumar montos de cargos seleccionados
    const seleccionado = selectedCargos.reduce((sum, item) => sum + (item.monto_aplicado || 0), 0);
    setTotalSeleccionado(seleccionado);
    // Usar SIEMPRE el recargo mostrado en el pie de página (window.__lastDisplayRecargos)
    let recargoSum = 0;
    if (typeof window !== 'undefined' && typeof window.__lastDisplayRecargos === 'number') {
      recargoSum = window.__lastDisplayRecargos;
    } else {
      recargoSum = selectedCargos.reduce((sum, item) => sum + (recargoMap[item.id_cargo] || 0), 0);
    }
    setTotalRecargo(recargoSum);
    setTotalConRecargo(seleccionado + recargoSum);
  }, [selectedCargos, recargoMap, paymentData.fecha_pago]);
  
  

  
  const [charges, setCharges] = useState([
    {
      id: 1,
      id_concepto: '',
      monto: '',
      descuentoBecaMonto: '0',
      descuentoBonificacion: '0',
      descuentoCondonacion: '0',
      fechaAplicacion: getFechaActual(),
      fechaVencimiento: '',
      motivo: '',
      categoriaConcepto: ''
    }
  ]);

  // Función para calcular monto neto con los nuevos campos de descuento
  const calcularMontoNetoConDescuentos = (monto, descuentoBeca, descuentoBonif, descuentoCondo) => {
    const montoBase = parseFloat(monto) || 0;
    const descBeca = parseFloat(descuentoBeca) || 0;
    const descBonif = parseFloat(descuentoBonif) || 0;
    const descCondo = parseFloat(descuentoCondo) || 0;
    const totalDescuentos = descBeca + descBonif + descCondo;
    const neto = montoBase - totalDescuentos;
    return neto > 0 ? neto : 0;
  };

  // Suma de cargos manuales creados en la parte superior (monto neto por cargo)
  const cargosSum = charges.reduce((sum, c) => {
    const neto = calcularMontoNetoConDescuentos(
      c.monto, 
      c.descuentoBecaMonto, 
      c.descuentoBonificacion, 
      c.descuentoCondonacion
    );
    return sum + (isNaN(neto) ? 0 : neto);
  }, 0);

  // Temporal: mostrar siempre 0 en la UI hasta que se conecte la lógica de cargos
  const cargosSumVisible = 0;

  // Monto base (selección de cargos o monto directo) y monto final incluyendo cargos manuales
  // Si hay cargos seleccionados, sumar recargo calculado dinámicamente; si no, solo monto directo + cargos manuales
  // Usar totalSeleccionado + totalRecargo para reflejar el recargo mostrado en la UI
  const montoBase = selectedCargos.length > 0 ? (totalSeleccionado + totalRecargo) : (parseFloat(paymentData.monto_directo) || 0);
  const montoARequerir = selectedCargos.length > 0
    ? (totalSeleccionado + totalRecargo + parseFloat(cargosSumVisible || 0))
    : (parseFloat(paymentData.monto_directo) || 0) + parseFloat(cargosSumVisible || 0);

  // Cargar conceptos y ciclos al montar
  useEffect(() => {
    loadConceptos(cicloFiltroConceptos);
    loadCiclos();
    loadReciboConfig();
  }, [campusId]);
  
  // Recargar conceptos cuando cambie el filtro de ciclo
  useEffect(() => {
    if (ciclos.length > 0) {
      loadConceptos(cicloFiltroConceptos);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cicloFiltroConceptos]);

  // Cargar beca del estudiante cuando cambie el estudiante
  useEffect(() => {
    loadBecaEstudiante();
  }, [student?.id_estudiante, student?.id]);

  // Cargar beca activa del estudiante
  const loadBecaEstudiante = async () => {
    // El campo puede venir como id_estudiante o como id dependiendo del serializer
    const estudianteId = student?.id_estudiante || student?.id;
    if (!estudianteId) {
      console.log('[ChargeForm] No hay estudiante seleccionado, omitiendo carga de beca');
      return;
    }
    try {
      console.log(`[ChargeForm] Cargando beca para estudiante ID: ${estudianteId}`);
      const response = await getBecaEstudiante(estudianteId);
      console.log('[ChargeForm] Respuesta de beca del estudiante:', response);
      // Usar becas_activas (array de todas las becas) si está disponible
      if (response?.tiene_beca && response?.becas_activas && response.becas_activas.length > 0) {
        console.log('[ChargeForm] Becas activas encontradas:', response.becas_activas.length);
        response.becas_activas.forEach((beca, idx) => {
          console.log(`[ChargeForm] Beca ${idx + 1}:`, {
            nombre: beca.nombre_beca,
            porcentaje: beca.porcentaje_otorgado,
            monto_asignado: beca.monto_beca_asignado,
            tipo: beca.tipo_beca
          });
        });
        // Guardar el array completo de becas activas
        setBecaEstudiante(response.becas_activas);
      } else if (response?.tiene_beca && response?.beca_activa) {
        // Fallback para compatibilidad: convertir una sola beca en array
        console.log('[ChargeForm] (Fallback) Una sola beca activa encontrada:', response.beca_activa.nombre_beca);
        setBecaEstudiante([response.beca_activa]);
      } else {
        console.log('[ChargeForm] El estudiante no tiene beca activa');
        setBecaEstudiante(null);
      }
    } catch (err) {
      console.error('Error al cargar beca del estudiante:', err);
      setBecaEstudiante(null);
    }
  };

  const loadReciboConfig = async () => {
    try {
      const config = await getConfigRecibos();
      console.log('Valor recibido de config.cc_emails:', config && config.cc_emails);
      if (config && Array.isArray(config.cc_emails)) {
        const ccFiltrados = config.cc_emails.filter((e) => !!e);
        setReciboCcEmails(ccFiltrados);
        console.log('Valor final de reciboCcEmails:', ccFiltrados);
      } else {
        setReciboCcEmails([]);
        console.log('Valor final de reciboCcEmails: []');
      }
    } catch (err) {
      console.error('Error al obtener configuración de recibos:', err);
      setReciboCcEmails([]);
      console.log('Valor final de reciboCcEmails: [] (error)');
    } finally {
      setReciboCcLoaded(true);
    }
  };

  const loadConceptos = async (cicloFiltro = '') => {
    try {
      const filters = { campus: campusId };
      // Filtrar por sección académica del estudiante
      if (student?.seccion_id) {
        filters.seccion = student.seccion_id;
      }
      // Filtrar por ciclo escolar si se seleccionó
      if (cicloFiltro) {
        // Buscar el ciclo por su ID para enviarlo al backend
        const cicloObj = ciclos.find(c => c.id_ciclo_escolar === parseInt(cicloFiltro) || c.id_ciclo_escolar === cicloFiltro);
        if (cicloObj) {
          // Enviar AMBOS: ID del ciclo (para filtro robusto) y nombre del ciclo (como backup)
          filters.id_ciclo_escolar = cicloObj.id_ciclo_escolar;
          filters.ciclo_escolar = cicloObj.ciclo || cicloObj.nombre_ciclo;
        }
      }
      const response = await getConceptosActivos(filters);
      if (response.success) {
        setConceptos(response.conceptos || []);
      }
    } catch (err) {
      console.error('Error al cargar conceptos:', err);
      setError('No se pudieron cargar los conceptos de cobro');
    }
  };

  const loadCiclos = async () => {
    try {
    // Cargar todos los ciclos para mostrarlos en el listado
    let response = await getCiclosEscolares();

    // Ordenar descendente por nombre de ciclo
    const ciclosOrdenados = (response || []).slice().sort((a, b) => {
      const nameA = (a.ciclo || a.nombre_ciclo || '').toUpperCase();
      const nameB = (b.ciclo || b.nombre_ciclo || '').toUpperCase();
      return nameB.localeCompare(nameA);
    });

    setCiclos(ciclosOrdenados);
	  
    // Seleccionar ciclo: priorizar el vigente que coincida con el ciclo del estudiante, si aplica
    if (ciclosOrdenados.length > 0) {
    if (student.ciclo) {
      const cicloActual = ciclosOrdenados.find(c => c.ciclo === student.ciclo);
      if (cicloActual) {
      setCicloSeleccionado(cicloActual.id_ciclo_escolar);
      } else {
      setCicloSeleccionado(ciclosOrdenados[0].id_ciclo_escolar);
      }
    } else {
      setCicloSeleccionado(ciclosOrdenados[0].id_ciclo_escolar);
    }

    // Seleccionar automáticamente el ciclo vigente en el filtro de conceptos
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const cicloVigenteObj = ciclosOrdenados.find(c => {
      const inicio = c.fecha_inicio ? new Date(c.fecha_inicio + 'T00:00:00') : null;
      const fin = c.fecha_fin ? new Date(c.fecha_fin + 'T00:00:00') : null;
      if (!inicio || !fin) return false;
      return hoy >= inicio && hoy <= fin;
    });
    if (cicloVigenteObj) {
      setCicloFiltroConceptos(cicloVigenteObj.id_ciclo_escolar);
    }
    }
    } catch (err) {
      console.error('Error al cargar ciclos:', err);
      setError('No se pudieron cargar los ciclos escolares');
    }
  };

  const handleAddCharge = () => {
    setCharges([...charges, {
      id: charges.length + 1,
      id_concepto: '',
      monto: '',
      descuentoBecaMonto: '0',
      descuentoBonificacion: '0',
      descuentoCondonacion: '0',
      fechaAplicacion: getFechaActual(),
      fechaVencimiento: '',
      motivo: '',
      categoriaConcepto: ''
    }]);
  };

  // Función para calcular descuento por múltiples becas
  // NUEVA LÓGICA: 
  // 1. PRIMER CRITERIO: Ordenar por fecha_inicio ASC (más antigua primero)
  // 2. SEGUNDO CRITERIO: Si misma fecha -> primero porcentaje (mayor a menor), luego monto
  const calcularDescuentoMultiplesBecas = (montoBase, becas, esCampusTeran) => {
    if (!becas || becas.length === 0 || montoBase <= 0) {
      return { descuentoTotal: 0, desglose: [] };
    }

    let descuentoTotal = 0;
    const desglose = [];
    let saldoRestante = montoBase;

    // CASO CAMPUS TERÁN: Sumar todos los montos de beca
    if (esCampusTeran) {
      becas.forEach(beca => {
        let montoBeca = 0;
        if (beca.monto_beca_asignado) {
          montoBeca = parseFloat(beca.monto_beca_asignado);
        } else if (beca.monto_beca_calculado) {
          montoBeca = parseFloat(beca.monto_beca_calculado) / 4;
        } else if (beca.porcentaje_otorgado) {
          montoBeca = (montoBase * parseFloat(beca.porcentaje_otorgado)) / 100;
        }
        if (montoBeca > 0) {
          descuentoTotal += montoBeca;
          desglose.push({
            nombre: beca.nombre_beca,
            tipo: 'MONTO',
            descuento: montoBeca
          });
        }
      });
      // No puede superar el monto original
      if (descuentoTotal > montoBase) descuentoTotal = montoBase;
      return { descuentoTotal, desglose };
    }

    // ================================================================
    // NUEVO ORDENAMIENTO: Por fecha_inicio ASC, luego por tipo/valor
    // ================================================================
    // Función auxiliar para parsear fecha
    const parsearFecha = (fecha) => {
      if (!fecha) return new Date(1900, 0, 1); // Fecha muy antigua por defecto
      if (fecha instanceof Date) return fecha;
      if (typeof fecha === 'string') {
        const parsed = new Date(fecha);
        return isNaN(parsed.getTime()) ? new Date(1900, 0, 1) : parsed;
      }
      return new Date(1900, 0, 1);
    };

    // Agrupar becas por fecha_inicio
    const becasPorFecha = {};
    becas.forEach(beca => {
      const fechaObj = parsearFecha(beca.fecha_inicio);
      const fechaKey = fechaObj.toISOString().split('T')[0];
      if (!becasPorFecha[fechaKey]) {
        becasPorFecha[fechaKey] = [];
      }
      becasPorFecha[fechaKey].push(beca);
    });

    // Ordenar las fechas de más antigua a más reciente
    const fechasOrdenadas = Object.keys(becasPorFecha).sort((a, b) => new Date(a) - new Date(b));

    // Para cada fecha, ordenar: porcentaje (mayor a menor) primero, luego monto
    const becasOrdenadas = [];
    fechasOrdenadas.forEach(fecha => {
      const becasEnFecha = becasPorFecha[fecha];
      
      // Separar en porcentaje y monto
      const porcentajeEnFecha = becasEnFecha.filter(b => 
        b.porcentaje_otorgado && parseFloat(b.porcentaje_otorgado) > 0 && 
        !(b.monto_beca_asignado && parseFloat(b.monto_beca_asignado) > 0)
      );
      const montoEnFecha = becasEnFecha.filter(b => 
        b.monto_beca_asignado && parseFloat(b.monto_beca_asignado) > 0
      );
      
      // Ordenar porcentaje de mayor a menor
      porcentajeEnFecha.sort((a, b) => parseFloat(b.porcentaje_otorgado) - parseFloat(a.porcentaje_otorgado));
      
      // Agregar primero porcentaje, luego monto
      becasOrdenadas.push(...porcentajeEnFecha, ...montoEnFecha);
    });

    console.log('[ChargeForm] Becas ordenadas por fecha_inicio:', becasOrdenadas.map(b => ({
      nombre: b.nombre_beca,
      fecha_inicio: b.fecha_inicio,
      tipo: b.monto_beca_asignado ? 'MONTO' : 'PORCENTAJE'
    })));

    // ================================================================
    // APLICAR BECAS EN ORDEN (fecha_inicio -> tipo -> valor)
    // ================================================================
    becasOrdenadas.forEach(beca => {
      if (saldoRestante <= 0) return;

      const tieneMonto = beca.monto_beca_asignado && parseFloat(beca.monto_beca_asignado) > 0;
      const tienePorcentaje = beca.porcentaje_otorgado && parseFloat(beca.porcentaje_otorgado) > 0;

      if (tieneMonto) {
        // Beca de monto fijo
        const montoBeca = parseFloat(beca.monto_beca_asignado);
        const descuentoAplicado = Math.min(montoBeca, saldoRestante);
        
        if (descuentoAplicado > 0) {
          descuentoTotal += descuentoAplicado;
          saldoRestante -= descuentoAplicado;
          desglose.push({
            nombre: beca.nombre_beca,
            tipo: 'MONTO',
            fecha_inicio: beca.fecha_inicio,
            monto: montoBeca,
            descuento: descuentoAplicado
          });
        }
      } else if (tienePorcentaje) {
        // Beca de porcentaje
        const porcentaje = parseFloat(beca.porcentaje_otorgado);
        let descuentoAplicado = 0;

        // Si tiene monto_beca_calculado, usar distribución en 4 parcialidades
        if (beca.monto_beca_calculado) {
          const montoBecaMensual = parseFloat(beca.monto_beca_calculado) / 4;
          descuentoAplicado = Math.min(montoBecaMensual, saldoRestante);
        } else {
          // Calcular descuento como porcentaje del SALDO RESTANTE
          descuentoAplicado = (saldoRestante * porcentaje) / 100;
        }

        if (descuentoAplicado > 0) {
          descuentoTotal += descuentoAplicado;
          saldoRestante -= descuentoAplicado;
          desglose.push({
            nombre: beca.nombre_beca,
            tipo: 'PORCENTAJE',
            fecha_inicio: beca.fecha_inicio,
            porcentaje: porcentaje,
            descuento: descuentoAplicado
          });
        }
      }
    });

    return { descuentoTotal: Math.round(descuentoTotal * 100) / 100, desglose };
  };

  const handleChargeChange = (index, field, value) => {
    const newCharges = [...charges];

    if (field === 'monto') {
      if (value === '') {
        newCharges[index][field] = '';
        setCharges(newCharges);
        return;
      }

      if (!/^\d*(\.\d{0,2})?$/.test(value)) {
        return;
      }

      const montoParsed = parseFloat(value);
      if (!Number.isNaN(montoParsed) && montoParsed < 0) {
        return;
      }
    }

    newCharges[index][field] = value;
    
    // Si cambia el concepto, actualizar el monto automáticamente con el precio_base
    // y aplicar descuento de beca si la SUBCATEGORÍA está entre P1 y P5
    if (field === 'id_concepto' && value) {
      const concepto = conceptos.find(c => c.id === parseInt(value));
      console.log('[ChargeForm] Concepto seleccionado:', concepto);
      console.log('[ChargeForm] Estado actual de becasEstudiante:', becaEstudiante);
      
      if (concepto) {
        // Cargar monto del concepto
        if (concepto.precio_base) {
          newCharges[index].monto = concepto.precio_base.toString();
        }
        
        // Guardar subcategoría del concepto (se usa para lógica de parcialidades P1-P5)
        const subcategoria = (concepto.subcategoria || '').toUpperCase().trim();
        newCharges[index].categoriaConcepto = subcategoria;
        
        // FECHA DE VENCIMIENTO AUTOMÁTICA
        // Si la subcategoría NO es una parcialidad (P1-P5), fecha de vencimiento = fecha de aplicación
        const esParcialidad = ['P1', 'P2', 'P3', 'P4', 'P5'].includes(subcategoria);
        if (!esParcialidad) {
          newCharges[index].fechaVencimiento = newCharges[index].fechaAplicacion || getFechaActual();
        }
        
        // ================================================================
        // NUEVA LÓGICA: Calcular descuento por múltiples becas
        // ================================================================
        const precioBase = parseFloat(concepto.precio_base) || 0;
        
        // becaEstudiante ahora es un array de becas activas
        const becasActivas = Array.isArray(becaEstudiante) ? becaEstudiante : [];
        
        // Aplicar becas solo cuando la SUBCATEGORÍA del concepto es una parcialidad (P1-P5)
        if (esParcialidad && becasActivas.length > 0) {
          const resultado = calcularDescuentoMultiplesBecas(precioBase, becasActivas, campusEsTeran);
          
          newCharges[index].descuentoBecaMonto = resultado.descuentoTotal.toFixed(2);
          console.log(`[ChargeForm] Descuento calculado por múltiples becas: $${resultado.descuentoTotal}`);
          console.log(`[ChargeForm] Desglose:`, resultado.desglose);
        } else {
          // Si no es parcialidad P1-P5 o no tiene becas, resetear descuento
          newCharges[index].descuentoBecaMonto = '0';
        }
        
        // Resetear otros descuentos
        newCharges[index].descuentoBonificacion = '0';
        newCharges[index].descuentoCondonacion = '0';
      }
    }
    
    // Si cambia la fecha de aplicación y la subcategoría no es una parcialidad (P1-P5), actualizar fecha de vencimiento
    if (field === 'fechaAplicacion') {
      const subcategoria = (newCharges[index].categoriaConcepto || '').toUpperCase();
      const esParcialidad = ['P1', 'P2', 'P3', 'P4', 'P5'].includes(subcategoria);
      if (!esParcialidad) {
        newCharges[index].fechaVencimiento = value;
      }
    }
    
    setCharges(newCharges);
  };

  // Previsualizar PDF en nueva pestaña
  const handlePreview = async () => {
    // Validar campos obligatorios
    // Usar el monto correcto según el flujo: cargos seleccionados o pago directo
    const monto = selectedCargos.length > 0 ? totalSeleccionado : paymentData.monto_directo;
    const formaPago = paymentData.forma_pago;
    const formaPagoLabel = FORMA_PAGO_LABELS[formaPago] || formaPago;
    const fechaPago = paymentData.fecha_pago;
    if (!formaPago) {
      alert('Debe seleccionar una forma de pago.');
      return;
    }
    if (!fechaPago) {
      alert('Debe seleccionar una fecha de pago.');
      return;
    }
    if (!monto || isNaN(parseFloat(monto)) || parseFloat(monto) <= 0) {
      alert('Debe ingresar un monto a pagar válido.');
      return;
    }

    let cargosParaPDF = [];
    const esPagoDirecto = selectedCargos.length === 0;
    let montoTotalAplicar = 0;
    if (esPagoDirecto) {
      montoTotalAplicar = parseFloat(paymentData.monto_directo) || 0;
      cargosParaPDF = [
        {
          cantidad: 1,
          monto: montoTotalAplicar,
          motivo: 'Pago a cuenta',
          id_concepto: 'PAGO-DIRECTO',
          monto_recargo: 0,
          importe: montoTotalAplicar,
        },
      ];
    } else if (selectedChargesForReceipt.length > 0) {
      // Tomar recargo directamente de la UI (window.__lastAdeudosPendientes) si existe
      let adeudosPendientesUI = [];
      if (typeof window !== 'undefined' && Array.isArray(window.__lastAdeudosPendientes)) {
        adeudosPendientesUI = window.__lastAdeudosPendientes;
      }
      cargosParaPDF = selectedChargesForReceipt.map((cargo) => {
        // Buscar el recargo en la UI por id_cargo
        let recargoUI = 0;
        if (adeudosPendientesUI.length > 0 && cargo.id_cargo) {
          const adeudoUI = adeudosPendientesUI.find(a => a.id === cargo.id_cargo);
          if (adeudoUI && adeudoUI.monto_recargo !== undefined) {
            recargoUI = adeudoUI.monto_recargo;
          }
        }
        return {
          ...cargo,
          monto_recargo: recargoUI,
          importe: (cargo.monto || 0) + recargoUI,
        };
      });
    } else {
      cargosParaPDF = charges
        .filter((c) => c.id_concepto && c.monto)
        .map((c) => ({
          ...c,
          monto: parseFloat(c.monto) || 0,
          cantidad: parseInt(c.cantidad) || 1,
          monto_recargo: recargoMap[c.id_cargo] || 0,
          importe: (parseFloat(c.monto) || 0) + (recargoMap[c.id_cargo] || 0),
        }));
    }

    if (cargosParaPDF.length === 0) {
      alert('No hay datos suficientes para generar el recibo.');
      return;
    }

    // Determinar folio para previsualización sin tocar la BD
    let folioPreview = '';
    try {
      if (esPagoDirecto) {
        // Para pagos directos el backend usa formato DIRECTO-YYYYmmddHHMMSS
        const now = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const ts = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        folioPreview = `DIRECTO-${ts}`;
      } else {
        // Intentar derivar el código/categoría del primer cargo para pedir el folio formateado al backend
        const cargo = (selectedChargesForReceipt.length > 0) ? selectedChargesForReceipt[0] : (charges.find(c => c.id_concepto && c.monto) || null);
        let codigo_concepto = '';
        if (cargo) {
          // El campo id_concepto puede venir como id numérico o como código; manejar ambos casos
          if (cargo.id_concepto && Number.isInteger(Number(cargo.id_concepto))) {
            // Buscar concepto por id
            const found = conceptos.find(c => Number(c.id) === Number(cargo.id_concepto));
            if (found) codigo_concepto = found.codigo || found.id || '';
          } else if (cargo.id_concepto) {
            // Puede ser el código directamente
            codigo_concepto = String(cargo.id_concepto);
          }
        }

        // Llamar al endpoint que devuelve el folio formateado sin actualizar consecutivo
        const params = { id_campus: campusId };
        // Enviar tanto el código como el id del concepto si están disponibles.
        if (codigo_concepto) params.codigo_concepto = codigo_concepto;
        // Si el campo id_concepto es numérico, pasar también como id_concepto
        if (cargo && cargo.id_concepto && Number.isInteger(Number(cargo.id_concepto))) {
          params.id_concepto = Number(cargo.id_concepto);
        }

        const res = await axios.get('/api/financiero/next-folio-by-concept/', { params });
        if (res && res.data && res.data.folio_formateado) {
          folioPreview = res.data.folio_formateado;
        } else if (res && res.data && res.data.next_folio) {
          // Fallback: devolver número sin formato
          folioPreview = String(res.data.next_folio);
        } else {
          folioPreview = '(Simulado)';
        }
      }
    } catch (err) {
      console.error('Error obteniendo folio para previsualización:', err);
      folioPreview = '(Simulado)';
    }

    try {
      const cajeroNombre = user?.nombre_completo || user?.nombre || user?.usuario || user?.email || '';
      const dataUri = await generateReciboPDF({
        student,
        lugar: student.campus || '',
        charges: cargosParaPDF,
        paymentData: {
          forma_pago: formaPagoLabel,
          referencia_bancaria: paymentData.referencia_bancaria,
          observaciones: paymentData.observaciones,
          codigo_recibo: folioPreview, // Usar folio obtenido para previsualización
          cajero: cajeroNombre,
        },
        isEmail: false // Para que sume igual que email, usando la columna IMPORTE
      });
      if (dataUri) {
        // Convertir base64 a Blob y abrir en nueva pestaña con título personalizado
        const base64 = dataUri.split(',')[1];
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        // Abrir nueva ventana y establecer el título
        const win = window.open();
        if (win) {
          win.document.title = 'Recibo de Pago';
          win.document.body.style.margin = '0';
          win.document.body.innerHTML = `<iframe src='${blobUrl}' width='100%' height='100%' style='border:none;position:fixed;top:0;left:0;bottom:0;right:0;width:100vw;height:100vh;'></iframe>`;
        } else {
          // Fallback si no se puede manipular la ventana
          window.open(blobUrl, '_blank');
        }
      } else {
        alert('No se pudo generar el PDF de previsualización.');
      }
    } catch (err) {
      console.error('Error al generar el PDF de previsualización:', err);
      alert('Ocurrió un error al generar el PDF de previsualización.');
    }
  };

  // Función para obtener el siguiente folio de recibo
  async function fetchNextFolioRecibo({ categoria, id_campus, clave = '' }) {
    try {
      const res = await axios.get('/api/financiero/next-folio/', {
        params: { categoria, id_campus, clave }
      });
      return res.data.next_folio;
    } catch (err) {
      console.error('Error obteniendo folio simulado:', err);
      return null;
    }
  }

  // Nueva: obtener folio formateado por concepto/código (no modifica la BD)
  async function fetchNextFolioReciboByConcept({ codigo_concepto, id_concepto, id_campus }) {
    try {
      const params = {};
      if (codigo_concepto) params.codigo_concepto = codigo_concepto;
      if (id_concepto) params.id_concepto = id_concepto;
      if (id_campus) params.id_campus = id_campus;
      const res = await axios.get('/api/financiero/next-folio-by-concept/', { params });
      return res.data.folio_formateado || res.data.next_folio || null;
    } catch (err) {
      console.error('Error obteniendo folio formateado por concepto:', err);
      return null;
    }
  }

  // Función principal: Aplica pagos - puede ser a cargos seleccionados o pago directo
  const handleApply = async () => {
        // Antes de armar el payload, recalcular y actualizar monto_recargo en los adeudos si la fecha de pago cambió
        try {
          if (typeof window !== 'undefined' && Array.isArray(window.__lastAdeudosPendientes)) {
            const adeudosPendientes = window.__lastAdeudosPendientes;
            // Buscar si la fecha de pago actual es diferente a la última usada para calcular recargos
            // y si es así, recalcular el recargo para cada adeudo
            if (selectedCargos.length > 0 && adeudosPendientes.length > 0 && paymentData.fecha_pago) {
              // Obtener categorías de recargo desde el primer PendingChargesSection
              let categoriasRecargo = [];
              if (window.__lastCategoriasRecargo && Array.isArray(window.__lastCategoriasRecargo)) {
                categoriasRecargo = window.__lastCategoriasRecargo;
              }
              // Función de cálculo
              const { calcularRecargoCategoria } = require('../../api/recargos');
              adeudosPendientes.forEach(adeudo => {
                let recargo = 0;
                if (categoriasRecargo.length > 0 && typeof calcularRecargoCategoria === 'function') {
                  const cat = categoriasRecargo.find(c => String(c.categoria || c.nombre_categoria).trim().toUpperCase() === String(adeudo.categoria || adeudo.nombre_categoria || '').trim().toUpperCase());
                  if (cat) {
                    try {
                      const fechaPago = paymentData && paymentData.fecha_pago ? paymentData.fecha_pago : new Date();
                      const fechaVenc = adeudo.fecha_vencimiento || new Date();
                      recargo = calcularRecargoCategoria(cat, fechaPago, fechaVenc) || 0;
                    } catch {}
                  }
                }
                adeudo.monto_recargo = recargo;
              });
            }
          }
        } catch (err) {
          // No interrumpir el flujo si falla el recálculo
          console.error('Error recalculando recargos antes de aplicar pago:', err);
        }
    setError(null);
    let timeoutId = null;
    let timeoutTriggered = false;
    
    // Validar forma de pago
    if (!paymentData.forma_pago) {
      alert('Debe seleccionar una forma de pago');
      return;
    }
    
    // Calcular el monto total a aplicar
    // Si hay cargos seleccionados, usar la suma de esos montos
    // Si no hay cargos seleccionados, usar el monto directo ingresado
    let montoTotalAplicar = 0;
    const esPagoDirecto = selectedCargos.length === 0;
    if (esPagoDirecto) {
      montoTotalAplicar = parseFloat(paymentData.monto_directo) || 0;
    } else {
      montoTotalAplicar = selectedCargos.reduce((sum, cargo) => sum + (cargo.monto_aplicado || 0), 0);
    }
    // Incluir cargos manuales (temporalmente mostramos 0 hasta conectar la lógica)
    montoTotalAplicar = parseFloat(montoTotalAplicar || 0) + parseFloat(cargosSumVisible || 0);
    // Redondear a 2 decimales para evitar errores de precisión flotante en el backend (max_digits=10)
    montoTotalAplicar = Math.round(montoTotalAplicar * 100) / 100;
    if (montoTotalAplicar <= 0) {
      if (esPagoDirecto) {
        alert('Debe ingresar un monto a pagar mayor a 0.');
      } else {
        alert('El monto total a aplicar debe ser mayor a 0.\nVerifique los montos en la columna "Monto a Aplicar" de la tabla.');
      }
      return;
    }

    // Calcular el recargo de cada cargo seleccionado justo antes de armar el payload
    let categoriasRecargo = [];
    if (typeof window !== 'undefined' && Array.isArray(window.__lastCategoriasRecargo)) {
      categoriasRecargo = window.__lastCategoriasRecargo;
    }
    const calcularRecargoCategoria = require('../../api/recargos').calcularRecargoCategoria;
    let adeudosPendientes = [];
    if (typeof window !== 'undefined' && Array.isArray(window.__lastAdeudosPendientes)) {
      adeudosPendientes = window.__lastAdeudosPendientes;
    }
    const aplicacionesConRecargo = esPagoDirecto ? [] : selectedCargos.map(item => {
        // LOG 2: selectedCargos antes de armar aplicacionesConRecargo
        console.log('[DEBUG] selectedCargos antes de armar aplicacionesConRecargo:', selectedCargos);

        // LOG 2b: recargoMap que ChargeForm recibió desde PendingChargesSection
        console.log('[DEBUG] recargoMap en ChargeForm (estado local):', recargoMap);

        // LOG 2c: adeudos pendientes globales en window
        if (typeof window !== 'undefined' && Array.isArray(window.__lastAdeudosPendientes)) {
          console.log('[DEBUG] window.__lastAdeudosPendientes:', window.__lastAdeudosPendientes);
        }

        // LOG 2d: mostrar recargoMap por cada cargo seleccionado (lookup detallado)
        selectedCargos.forEach(sc => {
          try {
            const lookup = recargoMap && (recargoMap[sc.id_cargo] !== undefined ? recargoMap[sc.id_cargo] : null);
            console.log(`[DEBUG] recargoMap[${sc.id_cargo}] =`, lookup, ' adeudo.monto_recargo (from window):', (typeof window !== 'undefined' && Array.isArray(window.__lastAdeudosPendientes) ? (window.__lastAdeudosPendientes.find(a=>a.id===sc.id_cargo)||{}).monto_recargo : undefined));
          } catch (e) {
            console.error('[DEBUG] error al inspeccionar recargoMap por cargo', sc, e);
          }
        });
      // Si el campus es Teran, no aplicar recargo (siempre 0)
      if (campusEsTeran) {
        return {
          id_cargo: item.id_cargo,
          monto_aplicado: Math.round((item.monto_aplicado || 0) * 100) / 100,
          monto_recargo: 0
        };
      }

      // Buscar el adeudo correspondiente
      const adeudo = adeudosPendientes.find(a => a.id === item.id_cargo);
      let monto_recargo = 0;
      if (adeudo) {
        // Lógica idéntica a mostrarRecargo de PendingChargesSection, usando recargosById recibido por prop
        const tryGet = (map, key) => {
          if (!map) return undefined;
          if (map[key] !== undefined && map[key] !== null) return map[key];
          const sk = String(key);
          if (map[sk] !== undefined && map[sk] !== null) return map[sk];
          return undefined;
        };

        const rawRecargoDesdeMapPadre = tryGet(recargoMap, adeudo.id);
        const recargoDesdeMapPadre = rawRecargoDesdeMapPadre !== undefined ? Number(rawRecargoDesdeMapPadre) || 0 : 0;

        const rawRecargoCalculadoFila = tryGet(recargosById, adeudo.id);
        let recargoCalculadoFila = rawRecargoCalculadoFila !== undefined ? Number(rawRecargoCalculadoFila) || 0 : 0;

        // Fallback adicional: si aún no existe en los maps, intentar tomarlo desde window.__lastRecargosById
        if ((!recargoCalculadoFila || recargoCalculadoFila === 0) && typeof window !== 'undefined' && window.__lastRecargosById) {
          const winVal = tryGet(window.__lastRecargosById, adeudo.id);
          if (winVal !== undefined) recargoCalculadoFila = Number(winVal) || 0;
        }

        const recargoGuardadoAdeudo = adeudo.monto_recargo !== undefined && adeudo.monto_recargo !== null ? Number(adeudo.monto_recargo) || 0 : 0;

        if (recargoDesdeMapPadre && recargoDesdeMapPadre !== 0) {
          monto_recargo = recargoDesdeMapPadre;
        } else if ((!recargoCalculadoFila || recargoCalculadoFila === 0) && recargoGuardadoAdeudo > 0) {
          monto_recargo = recargoGuardadoAdeudo;
        } else {
          monto_recargo = recargoCalculadoFila || 0;
        }
      } else {
        console.warn('[ChargeForm] No se encontró el adeudo para el cargo', item.id_cargo);
      }
      return {
        id_cargo: item.id_cargo,
        monto_aplicado: Math.round((item.monto_aplicado || 0) * 100) / 100,
        monto_recargo: Math.round((monto_recargo || 0) * 100) / 100
      };
    });
    // DEBUG: Log de aplicacionesConRecargo después de su declaración
    // LOG 3: aplicacionesConRecargo después de su declaración
    console.log('[DEBUG] aplicacionesConRecargo:', aplicacionesConRecargo);

    // Consultar configuración de recibos justo antes de mostrar confirmación
    let mensajeConfirmacion = '';
    let ccEmailsActual = [];
    let ccLoaded = false;
    let mostrarEnvioRecibo = true;
    // Si la fecha de pago es distinta a hoy, no mostrar mensaje de envío de recibo
    const hoy = getFechaActual();
    if (paymentData.fecha_pago !== hoy) {
      mostrarEnvioRecibo = false;
    }
    try {
      const config = await getConfigRecibos();
      console.log('Valor recibido de config.cc_emails (al aplicar pago):', config && config.cc_emails);
      if (config && Array.isArray(config.cc_emails)) {
        ccEmailsActual = config.cc_emails.filter((e) => !!e);
        ccLoaded = true;
        console.log('Valor final de ccEmailsActual (al aplicar pago):', ccEmailsActual);
      } else {
        ccEmailsActual = [];
        ccLoaded = false;
        console.log('Valor final de ccEmailsActual: [] (no array)');
      }
    } catch (err) {
      ccEmailsActual = [];
      ccLoaded = false;
      console.log('Valor final de ccEmailsActual: [] (error)', err);
    }

    if (student.email && mostrarEnvioRecibo) {
      mensajeConfirmacion += `📧 El recibo de pago se enviará a:\n   • ${student.email}`;
      if (ccLoaded) {
        if (ccEmailsActual && ccEmailsActual.length > 0) {
          const listaCc = ccEmailsActual.join(', ');
          mensajeConfirmacion += `\n   • (Copia: ${listaCc})`;
        } else {
          mensajeConfirmacion += `\n   • (Copia: ninguno)`;
        }
      } else {
        mensajeConfirmacion += `\n   • (Copia: cargando...)`;
      }
      mensajeConfirmacion += '\n\n';
    } else if (!student.email) {
      mensajeConfirmacion +=
        'El estudiante no tiene correo electrónico registrado, por lo que no se enviará el recibo por correo.\n\n';
    }

    const formaPagoLabel = FORMA_PAGO_LABELS[paymentData.forma_pago] || paymentData.forma_pago;
    if (esPagoDirecto) {
      mensajeConfirmacion +=
        `¿Confirma registrar el pago directo?\n` +
        `———————————————\n` +
        `• Monto: ${formatearMonto(montoTotalAplicar)}\n` +
        `• Forma de pago: ${formaPagoLabel}\n` +
        `———————————————\n` +
        `Nota: Este pago se registrará como un pago a cuenta del estudiante.\n\n` +
        `¿Desea continuar?`;
    } else {
      mensajeConfirmacion +=
        `¿Confirma aplicar el pago?\n` +
        `———————————————\n` +
        `• Cargos seleccionados: ${selectedCargos.length}\n` +
        `• Monto total a aplicar: ${formatearMonto(montoTotalAplicar)}\n` +
        `• Forma de pago: ${formaPagoLabel}\n` +
        `———————————————\n` +
        `Esta acción registrará el pago en el sistema.\n\n`
    }

    const confirmar = window.confirm(mensajeConfirmacion);
    
    if (!confirmar) return;

    // Antes de enviar al backend, validar que los cargos seleccionados existan
    // y pertenezcan efectivamente al estudiante (evitar error "Cargo X no encontrado").
    if (!esPagoDirecto && Array.isArray(selectedCargos) && selectedCargos.length > 0) {
      try {
        const detalle = await getDetalleEstudiantePagos(student.id);
        // El endpoint puede devolver los adeudos en `adeudos` o `adeudos_pendientes` o `cargos`
        const adeudosBackend = detalle && (detalle.adeudos || detalle.adeudos_pendientes || detalle.cargos || []);

        const adeudosIds = new Set((adeudosBackend || []).map(a => Number(a.id)));

        const cargosInvalidos = selectedCargos
          .map(c => Number(c.id_cargo))
          .filter(id => !adeudosIds.has(id));

        if (cargosInvalidos.length > 0) {
          alert(
            `No se pueden procesar los siguientes cargos porque no existen o no pertenecen al estudiante: ${cargosInvalidos.join(', ')}.\n\nActualice la lista de adeudos pendientes e intente nuevamente.`
          );
          return;
        }

        // Validar montos frente al saldo pendiente en backend
        const excedentes = selectedCargos.filter((c) => {
          const adeudo = (adeudosBackend || []).find(a => Number(a.id) === Number(c.id_cargo));
          if (!adeudo) return false;
          const saldo = parseFloat(adeudo.saldo_pendiente || adeudo.saldo || 0);
          return parseFloat(c.monto_aplicado || 0) > saldo;
        }).map(c => c.id_cargo);

        if (excedentes.length > 0) {
          alert(`El monto a aplicar excede el saldo pendiente para los cargos: ${excedentes.join(', ')}.\nRevise los montos e intente nuevamente.`);
          return;
        }
      } catch (valErr) {
        console.error('Error validando adeudos en backend antes de aplicar pago:', valErr);
        alert('No se pudo validar los adeudos en el servidor. Intente nuevamente o contacte a soporte.');
        return;
      }
    }


    setLoading(true);
    setApplyingPayment(true);

    // Timeout de 20 segundos para evitar quedarse en PROCESANDO...
    timeoutId = setTimeout(() => {
      timeoutTriggered = true;
      setLoading(false);
      setApplyingPayment(false);
      alert('El proceso de pago está tardando demasiado. Por favor, verifique su conexión o contacte a soporte.');
    }, 20000);

    try {
      // Obtener el id del usuario logeado
      const usuarioId = user && (user.id || user.id_usuario || user.idUser);
      // Construir el payload para crearPagoYAplicar
      const pagoData = {
        id_estudiante: student.id,
        id_campus: campusId,
        forma_pago: paymentData.forma_pago,
        referencia_bancaria: paymentData.referencia_bancaria || '',
        monto_recibido: montoTotalAplicar,
        fecha_pago: paymentData.fecha_pago,
        hora_pago: getHoraActual(),
        // Si no hay observaciones, solo poner texto por defecto en pago directo; para "APLICAR PAGO A CARGOS" dejar vacío
        observaciones:
          (paymentData.observaciones && paymentData.observaciones.trim().length > 0)
            ? paymentData.observaciones
            : (esPagoDirecto
                ? 'Pago directo registrado desde Cargos Manuales'
                : ''),
        aplicaciones: aplicacionesConRecargo,
        es_pago_directo: esPagoDirecto,
        usuario_creacion: usuarioId,
        monto_recargo: typeof window !== 'undefined' && typeof window.__lastDisplayRecargos === 'number' ? window.__lastDisplayRecargos : 0
      };
      
      // Log detallado del payload, especialmente monto_recargo de cada cargo
      if (Array.isArray(pagoData.aplicaciones)) {
        console.log('APLICAR PAGO - Payload enviado:', JSON.stringify(pagoData, null, 2));
        pagoData.aplicaciones.forEach((ap, idx) => {
          console.log(`Cargo #${idx + 1} -> id_cargo: ${ap.id_cargo}, monto_aplicado: ${ap.monto_aplicado}, monto_recargo: ${ap.monto_recargo}`);
        });
      } else {
        console.log('APLICAR PAGO - Payload enviado:', pagoData);
      }
      
      // Usar Promise.race para timeout
      const response = await Promise.race([
        crearPagoYAplicar(pagoData),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout de red')), 20000))
      ]);

      if (timeoutTriggered) return;
      clearTimeout(timeoutId);

      if (response.success) {
        // Limpiar selección y datos de pago para evitar reutilizar montos/ids del pago anterior
        setSelectedCargos([]);
        setSelectedChargesForReceipt([]);
        setPaymentData({
          forma_pago: '',
          referencia_bancaria: '',
          fecha_pago: getFechaActual(),
          observaciones: '',
          monto_directo: ''
        });

        // Generar recibo PDF con los cargos aplicados
        // 1) Preferimos los cargos seleccionados de la tabla de adeudos (flujo principal de aplicación de pagos)
        // 2) Si es pago directo, generamos un recibo con el monto pagado
        // 3) Si no hubiera, usamos los cargos creados manualmente en la parte superior
        let cargosParaPDF = [];

        if (esPagoDirecto) {
          // Para pago directo, crear un cargo virtual para el PDF
          cargosParaPDF = [{
            cantidad: 1,
            monto: montoTotalAplicar,
            motivo: 'Pago a cuenta',
            id_concepto: 'PAGO-DIRECTO',
            id_cargo: null
          }];
        } else if (selectedChargesForReceipt.length > 0) {
          // Usar el recargo calculado por la UI si existe y es mayor a 0
          const calcularRecargoCategoria = require('../../api/recargos').calcularRecargoCategoria;
          const categoriasRecargo = window.__lastCategoriasRecargo || [];
          const fechaPago = paymentData.fecha_pago || getFechaActual();
          const fechaVencimientoDefault = fechaPago;
          const categoriaDefault = categoriasRecargo[0]?.categoria || 'PARCIALIDADES';
          cargosParaPDF = selectedChargesForReceipt.map(c => {
            let recargo = (typeof c.monto_recargo === 'number' && c.monto_recargo > 0) ? c.monto_recargo : 0;
            let monto = typeof c.monto === 'number' ? c.monto : parseFloat(c.monto) || 0;
            if (recargo === 0) {
              // Asignar valores por defecto si faltan
              const adeudoCat = c.categoria || c.nombre_categoria || categoriaDefault;
              const fechaVencimiento = c.fecha_vencimiento || fechaVencimientoDefault;
              const cat = categoriasRecargo.find(cat => String(cat.categoria || cat.nombre_categoria).trim().toUpperCase() === String(adeudoCat).trim().toUpperCase());
              if (cat && typeof calcularRecargoCategoria === 'function') {
                try {
                  recargo = calcularRecargoCategoria(cat, fechaPago, fechaVencimiento);
                } catch (err) {
                  console.error('[DEBUG RECARGO] Error al calcular recargo:', err);
                }
              }
            }
            return {
              ...c,
              id_cargo: c.id_cargo || c.id || null,
              monto_recargo: recargo,
              monto
            };
          });
        } else {
          cargosParaPDF = charges
            .filter(c => c.id_concepto && c.monto)
            .map(c => ({
              ...c,
              monto: parseFloat(c.monto) || 0,
              cantidad: parseInt(c.cantidad) || 1,
              id_cargo: c.id_cargo || c.id || null,
              monto_recargo: c.monto_recargo !== undefined ? c.monto_recargo : (recargoMap[c.id_cargo || c.id] || 0)
            }));
        }

        let emailEnviado = null;
        let emailErrorMessage = '';
        // Solo enviar recibo por email si la fecha de pago es hoy
        const hoy = getFechaActual();
        let debeEnviarRecibo = true;
        if (paymentData.fecha_pago !== hoy) {
          debeEnviarRecibo = false;
        }
        // --- SIEMPRE DESCARGAR EL PDF ---
        if (cargosParaPDF.length > 0) {
          try {
            let idsAplicados = [];
            if (!esPagoDirecto && Array.isArray(selectedCargos)) {
              idsAplicados = selectedCargos.map(c => c.id_cargo || c.id);
            } else if (Array.isArray(cargosParaPDF) && cargosParaPDF.length === 1 && cargosParaPDF[0].id_concepto === 'PAGO-DIRECTO') {
              idsAplicados = [];
            }
            let cargosAplicados = [];
            if (!esPagoDirecto && idsAplicados.length > 0) {
              const { getCargosPorIds } = await import('../../api/cargosPorIds');
              cargosAplicados = await getCargosPorIds(idsAplicados);
              if (!cargosAplicados || !cargosAplicados.length) {
                alert('No se encontraron los cargos pagados en el backend. El recibo no se enviará para evitar errores. Actualice la lista de adeudos y verifique la operación.');
                return;
              }
            }
            // Log para verificar cargosAplicados y cargosPDF
            console.log('CARGOS APLICADOS:', cargosAplicados);
            // Mapear los datos para el PDF
            const cargosPDF = cargosAplicados.length > 0 ? cargosAplicados.map(c => ({
              ...c,
              monto: typeof c.monto === 'number' ? c.monto : parseFloat(c.monto) || 0,
              monto_recargo: typeof c.monto_recargo === 'number' ? c.monto_recargo : parseFloat(c.monto_recargo) || 0,
              cantidad: c.cantidad || 1,
              descripcion_concepto: c.descripcion_concepto || c.descripcion || '',
              motivo: c.motivo || '',
              id_concepto: c.id_concepto || c.concepto || '',
              codigo_concepto: c.codigo_concepto || '',
              id_cargo: c.id_cargo || c.id || null
            })) : cargosParaPDF;
            console.log('CARGOS PDF:', cargosPDF);
            const cajeroNombre = user?.nombre_completo || user?.nombre || user?.usuario || user?.email || '';
            const dataUri = await generateReciboPDF({
              student,
              lugar: student.campus || '',
              charges: cargosPDF,
              paymentData: {
                forma_pago: FORMA_PAGO_LABELS[paymentData.forma_pago] || paymentData.forma_pago,
                referencia_bancaria: paymentData.referencia_bancaria,
                observaciones: paymentData.observaciones,
                codigo_recibo: response?.pago?.folio_recibo,
                cajero: cajeroNombre
              },
              isEmail: true
            });
            // Descargar automáticamente el PDF generado en el navegador
            if (dataUri && typeof window !== 'undefined') {
              try {
                const base64ForDownload = dataUri.includes(',') ? dataUri.split(',')[1] : dataUri;
                const byteCharacters = atob(base64ForDownload);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);

                const link = document.createElement('a');
                const fechaPagoSafe = (paymentData.fecha_pago || getFechaActual()).replace(/[^0-9]/g, '');
                const folioSafe = (response?.pago?.folio_recibo || 'recibo').toString().replace(/[^A-Za-z0-9_-]/g, '');
                link.href = url;
                link.download = `recibo_${fechaPagoSafe}_${folioSafe}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              } catch (downloadError) {
                console.error('Error al forzar la descarga del recibo PDF:', downloadError);
              }
            }
            // Envío por email solo si corresponde
            if (debeEnviarRecibo && dataUri && student.email) {
              const base64 = dataUri.includes(',') ? dataUri.split(',')[1] : dataUri;
              try {
                await enviarReciboCargosManuales({
                  toEmail: student.email,
                  pdfBase64: base64,
                  subject: 'Recibo de pago Universidad Lux',
                });
                emailEnviado = true;
              } catch (emailError) {
                console.error('Error al enviar el recibo por correo:', emailError);
                emailErrorMessage = emailError?.message || emailError?.toString() || '';
                emailEnviado = false;
              }
            }
          } catch (pdfError) {
            console.error('Error al generar el PDF de recibo:', pdfError);
          }
        }

        // Informar resultado del envío de correo y, si falla, no continuar con el flujo
        if (student.email && emailEnviado === false) {
          const detalle = emailErrorMessage ? `\n\nDetalle técnico: ${emailErrorMessage}` : '';
          alert(`❌ El pago se registró, pero ocurrió un error al enviar el recibo al correo: ${student.email}.${detalle}\n\nNo se continuará con el flujo hasta que el correo se envíe correctamente. Intente nuevamente más tarde o contacte a soporte.`);
          return;
        }

        if (student.email && emailEnviado === true && debeEnviarRecibo) {
          alert(`✅ El recibo de pago se envió correctamente al correo: ${student.email}`);
        }

        // Mostrar mensaje de éxito
        alert(`✅ ${response.message}`);

        // Refrescar la lista de adeudos pendientes y el historial de pagos
        setPendingChargesKey(prev => prev + 1);
        setHistorialPagosKey(prev => prev + 1);
        // Limpiar el formulario de cargos
        setCharges([{
          id: 1,
          id_concepto: '',
          monto: '',
          descuentoBecaMonto: '0',
          descuentoBonificacion: '0',
          descuentoCondonacion: '0',
          fechaAplicacion: getFechaActual(),
          fechaVencimiento: '',
          motivo: ''
        }]);
        // Limpiar selección de cargos y datos de pago
        setSelectedCargos([]);
        setPaymentData({
          forma_pago: '',
          referencia_bancaria: '',
          fecha_pago: getFechaActual(),
          observaciones: '',
          monto_directo: ''
        });
      } else {
        alert(`❌ Error al aplicar pago: ${response.message || 'Error desconocido'}`);
      }
    } catch (err) {
      if (!timeoutTriggered) clearTimeout(timeoutId);
      console.error('Error al aplicar pago:', err);
      alert(`❌ Error al aplicar pago: ${err.message || 'Error de conexión'}`);
    } finally {
      if (!timeoutTriggered) {
        setLoading(false);
        setApplyingPayment(false);
      }
    }
  };

  // Función para aplicar cargo sin redirigir - solo agrega el cargo y actualiza la tabla
  const handleApplyChargeOnly = async () => {
    setError(null);

    const normalizarCiclo = (v) => String(v || '').trim().toUpperCase();

    // Filtrar y validar cargos
    const usuarioId = user && (user.id || user.id_usuario || user.idUser);
    // Obtener nombre del campus para la lógica de becas
    const campusNombre = student?.campus_nombre || student?.campus || '';

    const cargosBase = charges.filter(c => c.id_concepto && c.monto);
    const cargosParaEnviar = [];

    for (const c of cargosBase) {
      const conceptoObj = getConceptoById(c.id_concepto);
      const cicloConcepto = conceptoObj?.ciclo;

      if (!conceptoObj) {
        alert('No se encontró el concepto seleccionado para uno de los cargos.');
        return;
      }

      if (!cicloConcepto) {
        alert(`El concepto "${conceptoObj.nombre || conceptoObj.nombre_concepto || conceptoObj.codigo || conceptoObj.codigo_concepto || c.id_concepto}" no tiene ciclo configurado.`);
        return;
      }

      const cicloObj = ciclos.find((ciclo) =>
        normalizarCiclo(ciclo.ciclo || ciclo.nombre_ciclo) === normalizarCiclo(cicloConcepto)
      );

      if (!cicloObj?.id_ciclo_escolar) {
        alert(`No se encontró el ciclo escolar "${cicloConcepto}" del concepto seleccionado en el catálogo de ciclos.`);
        return;
      }

      cargosParaEnviar.push({
        id_estudiante: student.id,
        id_concepto: parseInt(c.id_concepto),
        id_ciclo_escolar: parseInt(cicloObj.id_ciclo_escolar),
        monto: parseFloat(c.monto),
        cantidad: 1, // Siempre es 1
        // Nuevos campos de descuento
        descuento_beca_monto: parseFloat(c.descuentoBecaMonto) || 0,
        descuento_bonificacion: parseFloat(c.descuentoBonificacion) || 0,
        descuento_condonacion: parseFloat(c.descuentoCondonacion) || 0,
        campus_nombre: campusNombre,
        fecha_aplicacion: c.fechaAplicacion || new Date().toISOString().split('T')[0],
        fecha_vencimiento: c.fechaVencimiento || new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
        motivo: c.motivo || 'Cargo manual aplicado',
        usuario_creacion: usuarioId
      });
    }

    if (cargosParaEnviar.length === 0) {
      alert('Debe completar al menos un cargo con concepto y monto');
      return;
    }
    for (let i = 0; i < cargosParaEnviar.length; i++) {
      const validacion = validarCargo(cargosParaEnviar[i]);
      if (!validacion.valid) {
        alert(`Cargo ${i + 1}: ${validacion.errors.join(', ')}`);
        return;
      }
    }
    // --- Validación de duplicados antes de registrar el cargo ---
    try {
      const detalle = await getDetalleEstudiantePagos(student.id);
      // Puede venir como adeudos, cargos, o adeudos_pendientes
      const cargosExistentes = detalle?.cargos || detalle?.adeudos || detalle?.adeudos_pendientes || [];
      for (let cargoNuevo of cargosParaEnviar) {
        // Obtener el objeto del concepto para verificar la subcategoría
        const conceptoObj = getConceptoById(cargoNuevo.id_concepto);
        const subcategoria = (conceptoObj?.subcategoria || '').trim().toUpperCase();

        // Los conceptos con subcategoría CONSTANCIA sí pueden repetirse en el mismo periodo
        if (subcategoria === 'CONSTANCIA') continue;

        const yaExiste = cargosExistentes.some(c =>
          Number(c.id_concepto) === Number(cargoNuevo.id_concepto) &&
          Number(c.id_estudiante) === Number(cargoNuevo.id_estudiante) &&
          Number(c.id_ciclo_escolar) === Number(cargoNuevo.id_ciclo_escolar)
        );
        if (yaExiste) {
          // Construir mensaje con código y subcategoría del concepto
          let conceptoMsg = '';
          if (conceptoObj) {
            const codigo = conceptoObj.codigo_concepto || conceptoObj.codigo || '';
            const subcat = conceptoObj.subcategoria || '';
            if (codigo && subcat) {
              conceptoMsg = `${codigo} - ${subcat}`;
            } else if (codigo) {
              conceptoMsg = `${codigo}`;
            } else if (subcat) {
              conceptoMsg = `${subcat}`;
            } else {
              conceptoMsg = cargoNuevo.id_concepto;
            }
          } else {
            conceptoMsg = cargoNuevo.id_concepto;
          }
          window.alert(`Cargo previamente aplicado a Estudiante\n\nAdvertencia:\n- ⚠️ Ya existe un cargo pendiente para el concepto "${conceptoMsg}" en el ciclo escolar seleccionado.`);
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('Error validando cargos duplicados:', err);
      // Si hay error, continuar con el flujo normal
    }
    // --- Fin validación de duplicados ---
    setLoading(true);
    try {
      // Agregar la categoría a cada cargo antes de enviarlo
      const cargosConCategoria = cargosParaEnviar.map((cargo) => {
        const conceptoObj = getConceptoById(cargo.id_concepto);
        return {
          ...cargo,
          categoria: conceptoObj && conceptoObj.categoria ? String(conceptoObj.categoria).trim() : ''
        };
      });
      const response = await crearCargosManuales(cargosConCategoria);
      if (response.success) {
        alert(`✅ ${response.message || 'Cargo(s) creado(s) exitosamente'}\n\nEl cargo ha sido agregado al estudiante.`);
        setPendingChargesKey(prev => prev + 1);
        setCharges([{
          id: 1,
          id_concepto: '',
          monto: '',
          descuentoBecaMonto: '0',
          descuentoBonificacion: '0',
          descuentoCondonacion: '0',
          fechaAplicacion: getFechaActual(),
          fechaVencimiento: '',
          motivo: ''
        }]);
      } else {
        let mensaje = response.message || 'Error al crear cargo';
        if (response.errores && response.errores.length > 0) {
          mensaje += '\n\nAdvertencia:\n';
          response.errores.forEach(err => {
            // Detectar errores de duplicado y mostrar mensaje amigable
            if (err.errores && err.errores.duplicado) {
              mensaje += `- ⚠️ ${err.errores.duplicado}\n`;
            } else {
              mensaje += `- Cargo ${err.indice + 1}: ${JSON.stringify(err.errores)}\n`;
            }
          });
        }
        alert(mensaje);
      }
    } catch (err) {
      console.error('Error al aplicar cargo:', err);
      // Manejar errores de duplicado que vengan en la respuesta del backend
      if (err.details && err.details.errores) {
        let mensaje = err.details.message || 'Error al crear cargo';
        mensaje += '\n\n';
        err.details.errores.forEach(e => {
          if (e.errores && e.errores.duplicado) {
            mensaje += `⚠️ ${e.errores.duplicado}\n`;
          } else {
            mensaje += `- Cargo ${e.indice + 1}: ${JSON.stringify(e.errores)}\n`;
          }
        });
        alert(mensaje);
      } else {
        const mensajeError = err.message || 'Error al crear el cargo. Intente nuevamente.';
        alert(mensajeError);
      }
      setError(typeof err === 'string' ? err : (err.message || 'Error al crear cargo'));
    } finally {
      setLoading(false);
    }
  };

  const getConceptoById = (id) => {
    return conceptos.find(c => c.id === parseInt(id));
  };

	// Obtener el nombre del ciclo seleccionado, priorizando el ciclo vigente
	const cicloSeleccionadoObj = ciclos.find(
	  (c) => c.id_ciclo_escolar === cicloSeleccionado || c.id_ciclo_escolar === parseInt(cicloSeleccionado)
	);

	// Obtener el ciclo vigente según la fecha actual (independiente de la selección del usuario)
	const getCicloVigente = () => {
	  const hoy = new Date();
	  hoy.setHours(0, 0, 0, 0);
	  return ciclos.find(c => {
	    const inicio = c.fecha_inicio ? new Date(c.fecha_inicio + 'T00:00:00') : null;
	    const fin = c.fecha_fin ? new Date(c.fecha_fin + 'T00:00:00') : null;
	    if (!inicio || !fin) return false;
	    return hoy >= inicio && hoy <= fin;
	  });
	};
	const cicloVigente = getCicloVigente();

  return (
    <ErrorBoundary>
    <div className={styles.chargeFormContainer}>
      <button className={styles.backButton} onClick={onBack}>
        ← Regresar a búsqueda
      </button>

      {error && (
        <div className={styles.errorMessage}>
          {typeof error === 'object' && error !== null
            ? (error.$$typeof && error.type
                ? '[React Element: ' + (error.type.name || 'Unknown') + ']'
                : (() => { try { return JSON.stringify(error); } catch { return '[object Object]'; } })())
            : String(error)}
        </div>
      )}

      {/* Student Info Card */}
      <div className={styles.studentInfoCard}>
        <div className={styles.studentInfoHeader}>
          <h3>Estudiante: {student.nombre}</h3>
        </div>
        <div className={styles.studentInfoBody}>
          <div className={styles.studentAvatar}>
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" fill="#e0e0e0"/>
              <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" fill="#898d8d"/>
              <path d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21" fill="#898d8d"/>
            </svg>
          </div>
          <div className={styles.studentInfoGrid}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Matrícula:</span>
              <span className={styles.infoValue}>{student.matricula}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Email institucional:</span>
              <span className={styles.infoValue}>{student.email || 'N/A'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Estatus:</span>
              <span className={styles.infoValue}>{student.estatus_estudiante || 'N/A'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Sección:</span>
              <span className={styles.infoValue}>{student.seccion || 'N/A'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Institución:</span>
              <span className={styles.infoValue}>{student.institucion}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Programa:</span>
              <span className={styles.infoValue}>{student.programa || 'N/A'}</span>
            </div>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Campus:</span>
              <span className={styles.infoValue}>{student.campus}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Indicador de becas activas del estudiante */}
      {Array.isArray(becaEstudiante) && becaEstudiante.length > 0 && (
        <div style={{
          backgroundColor: '#d1fae5',
          border: '1px solid #10b981',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>🎓</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', color: '#065f46', marginBottom: '8px' }}>
              Becas Activas: {becaEstudiante.length}
            </div>
            {becaEstudiante.map((beca, idx) => (
              <div key={idx} style={{ 
                fontSize: '13px', 
                color: '#047857',
                backgroundColor: '#ecfdf5',
                padding: '6px 10px',
                borderRadius: '4px',
                marginBottom: '4px'
              }}>
                <strong>{beca.nombre_beca}</strong>
                {beca.porcentaje_otorgado && beca.porcentaje_otorgado > 0 && (
                  <span> - {beca.porcentaje_otorgado}%</span>
                )}
                {beca.monto_beca_asignado && parseFloat(beca.monto_beca_asignado) > 0 && (
                  <span> - ${parseFloat(beca.monto_beca_asignado).toLocaleString('es-MX', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                )}
              </div>
            ))}
            <div style={{ fontSize: '12px', color: '#059669', marginTop: '8px' }}>
              Se aplicará automáticamente a conceptos cuya subcategoría sea P1, P2, P3, P4 o P5
            </div>
          </div>
        </div>
      )}
      {/* Filtro de Ciclo Escolar para Conceptos */}
      <div className={styles.conceptCard} style={{ marginBottom: '16px' }}>
        <div className={styles.conceptHeader}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3>Filtros de Conceptos de Cobro</h3>
        </div>
        <div className={styles.conceptBody}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Sección Académica</label>
              <input 
                type="text"
                value={student.seccion || 'N/A'}
                readOnly
                className={styles.readOnlyInput}
                title="Los conceptos se filtran automáticamente por la sección del estudiante"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Filtrar por Ciclo Escolar</label>
              <select
                value={cicloFiltroConceptos}
                onChange={(e) => setCicloFiltroConceptos(e.target.value)}
                disabled={loading}
              >
                <option value="">Todos los ciclos</option>
                {ciclos.map((ciclo) => (
                  <option key={ciclo.id_ciclo_escolar} value={ciclo.id_ciclo_escolar}>
                    {ciclo.ciclo || ciclo.nombre_ciclo}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formGroup}>
              <label>Conceptos disponibles</label>
              <input 
                type="text"
                value={`${conceptos.length} concepto(s)`}
                readOnly
                className={styles.readOnlyInput}
              />
            </div>
          </div>
        </div>
      </div>
 
      {/* Charges List */}
      {Array.isArray(charges) && charges.map((charge, index) => (
        <div key={charge.id} className={styles.chargeSection}>
          {/* Datos del Concepto */}
          <div className={styles.conceptCard}>
            <div className={styles.conceptHeader}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="#ed8b00" strokeWidth="2"/>
                <path d="M9 11H15M12 8V14" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <h3>Datos del Concepto {charges.length > 1 ? `#${index + 1}` : ''}</h3>
            </div>
            <div className={styles.conceptBody}>
              <div className={styles.formRow}>
                <div className={styles.formGroup} style={{flex: 2}}>
                  <label>Concepto de Cobro *</label>
                  <Select
                    options={conceptos.map((concepto) => {
                      const subcat = typeof concepto.subcategoria === 'string' ? concepto.subcategoria.trim() : '';
                      return {
                        value: concepto.id,
                        label: `${concepto.codigo} - ${concepto.nombre}${subcat.length > 0 ? ' - ' + subcat : ''}`
                      };
                    })}
                    value={
                      conceptos
                        .map((concepto) => {
                          const subcat = typeof concepto.subcategoria === 'string' ? concepto.subcategoria.trim() : '';
                          return {
                            value: concepto.id,
                            label: `${concepto.codigo} - ${concepto.nombre}${subcat.length > 0 ? ' - ' + subcat : ''}`
                          };
                        })
                        .find((opt) => opt.value === Number(charge.id_concepto)) || null
                    }
                    onChange={(selected) => handleChargeChange(index, 'id_concepto', selected ? selected.value : '')}
                    isClearable
                    isDisabled={loading}
                    placeholder="Seleccione o busque un concepto"
                    noOptionsMessage={() => 'Sin resultados'}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 }),
                      menu: base => ({ ...base, zIndex: 9999 }),
                    }}
                    maxMenuHeight={350}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Monto *</label>
                  <input 
                    type="number"
                    step="0.01"
                    min="0"
                    value={typeof charge.monto === 'object' ? '' : charge.monto}
                    onChange={(e) => handleChargeChange(index, 'monto', e.target.value)}
                    placeholder="Ingrese monto"
                    disabled={loading}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Descuento $ por Beca</label>
                  <input 
                    type="number"
                    step="0.01"
                    min="0"
                    value={charge.descuentoBecaMonto || '0'}
                    readOnly
                    className={styles.readOnlyInput}
                    placeholder="Automático"
                    title="Calculado automáticamente según las becas activas del estudiante"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Descuento Bonificación $</label>
                  <input 
                    type="number"
                    step="0.01"
                    min="0"
                    value={charge.descuentoBonificacion || '0'}
                    onChange={(e) => handleChargeChange(index, 'descuentoBonificacion', e.target.value)}
                    placeholder="0.00"
                    disabled={loading}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Descuento Condonación $</label>
                  <input 
                    type="number"
                    step="0.01"
                    min="0"
                    value={charge.descuentoCondonacion || '0'}
                    onChange={(e) => handleChargeChange(index, 'descuentoCondonacion', e.target.value)}
                    placeholder="0.00"
                    disabled={loading}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Monto Neto</label>
                  <input 
                    type="text"
                    value={charge.monto ? 
                      calcularMontoNetoConDescuentos(
                        charge.monto, 
                        charge.descuentoBecaMonto, 
                        charge.descuentoBonificacion, 
                        charge.descuentoCondonacion
                      ).toFixed(2) : 
                      '0.00'}
                    readOnly
                    className={styles.readOnlyInput}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Configuración de Pago */}
          <div className={styles.paymentConfigCard}>
            <div className={styles.paymentConfigHeader}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="#ed8b00" strokeWidth="2"/>
                <path d="M2 10H22" stroke="#ed8b00" strokeWidth="2"/>
              </svg>
              <h3>Configuración de Pago</h3>
            </div>
            <div className={styles.paymentConfigBody}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Fecha de Aplicación *</label>
                  <div className={styles.dateInputWrapper}>
                    <input 
                      type="date"
                      value={charge.fechaAplicacion}
                      onChange={(e) => handleChargeChange(index, 'fechaAplicacion', e.target.value)}
                      disabled={loading}
                    />
                    <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="#898d8d" strokeWidth="2"/>
                      <path d="M16 2V6M8 2V6M3 10H21" stroke="#898d8d" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Fecha de Vencimiento *</label>
                  <div className={styles.dateInputWrapper}>
                    <input 
                      type="date"
                      value={charge.fechaVencimiento}
                      onChange={(e) => handleChargeChange(index, 'fechaVencimiento', e.target.value)}
                      disabled={loading}
                    />
                    <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" stroke="#898d8d" strokeWidth="2"/>
                      <path d="M16 2V6M8 2V6M3 10H21" stroke="#898d8d" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Motivo / Descripción */}
          <div className={styles.motivoCard}>
            <div className={styles.motivoHeader}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3>Motivo / Descripción</h3>
            </div>
            <div className={styles.motivoBody}>
                <textarea
                  value={typeof charge.motivo === 'object' ? '' : charge.motivo}
                  onChange={(e) => handleChargeChange(index, 'motivo', e.target.value)}
                  placeholder="Ingrese el motivo o descripción del cargo..."
                  rows="4"
                  disabled={loading}
                />
            </div>
          </div>
        </div>
      ))}

      {/* Botón Aplicar Cargo - Agrega el cargo sin redirigir */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        margin: '20px 0',
        padding: '16px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px dashed #dee2e6'
      }}>
        <button
          type="button"
          onClick={handleApplyChargeOnly}
          disabled={loading || !charges.some(c => c.id_concepto && c.monto)}
          style={{
            backgroundColor: '#ed8b00',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '12px 32px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading || !charges.some(c => c.id_concepto && c.monto) ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {loading ? 'PROCESANDO...' : 'APLICAR CARGO AL ESTUDIANTE'}
        </button>
      </div>

      {/* Sección de Adeudos Pendientes del Estudiante */}
      <PendingChargesSection 
        key={pendingChargesKey}
        studentId={student.id}
        recargoMap={recargoMap}
        campusEsTeran={campusEsTeran}
        onRecargosByIdChange={setRecargoMap}
        onChargeDeleted={(cargoId) => {
          setSelectedCargos(prev => {
            const next = (prev || []).filter(c => Number(c.id_cargo) !== Number(cargoId));
            if (Array.isArray(next) && next.length > 0) {
              setSelectedChargesForReceipt(prevRecibo => (prevRecibo || []).filter(r => Number(r.id_cargo) !== Number(cargoId)));
            } else {
              setSelectedChargesForReceipt([]);
            }
            return next;
          });
          setPaymentData({
            forma_pago: '',
            referencia_bancaria: '',
            fecha_pago: getFechaActual(),
            observaciones: '',
            monto_directo: ''
          });
          setPendingChargesKey(prev => prev + 1);
        }}
        onSelectionChange={handleSelectionChange}
        selectedCargosFromParent={selectedCargos}
        cargosSum={cargosSumVisible}
        fechaPago={paymentData.fecha_pago}
      />
      
      {/* Sección de Aplicación de Pago - Siempre visible para permitir pagos directos */}
        <div className={styles.paymentApplicationCard}>
          <div className={styles.paymentConfigHeader}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="#ed8b00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>Registrar Pago</h3>
          </div>
          <div className={styles.paymentConfigBody}>
            {selectedCargos.length > 0 ? (
              <div className={styles.paymentSummary}>
                <span className={styles.paymentSummaryLabel}>Cargos seleccionados: {selectedCargos.length}</span>
                <span className={styles.paymentSummaryTotal} style={{color: '#28a745', fontWeight: 'bold'}}>
                  Total a aplicar: {formatearMonto(montoARequerir)}
                </span>
                <span className={styles.paymentSummaryLabel} style={{marginLeft: '16px', fontSize: '12px', color: '#666'}}>
                  (Edite los montos en la columna "Monto a Aplicar" de la tabla)
                </span>
              </div>
            ) : (
              <div className={styles.paymentSummary} style={{backgroundColor: '#fff3cd', padding: '10px', borderRadius: '4px', marginBottom: '10px'}}>
                <span style={{color: '#856404'}}>
                  💡 Puede registrar un pago directo sin seleccionar cargos existentes. Ingrese el monto a pagar abajo.
                </span>
              </div>
            )}
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Monto a Pagar *</label>
                <input 
                  type="number"
                  step="0.01"
                  min="0"
                  value={
                    // Si hay cargos seleccionados, mostrar total seleccionado + cargos manuales
                    selectedCargos.length > 0
                      ? montoARequerir.toFixed(2)
                      : (
                          // Si no hay selección, mostrar monto_directo + cargos manuales (editable)
                          (paymentData.monto_directo && paymentData.monto_directo !== '')
                            ? ( (parseFloat(paymentData.monto_directo) || 0) + cargosSumVisible ).toFixed(2)
                            : (cargosSumVisible > 0 ? cargosSumVisible.toFixed(2) : '')
                        )
                  }
                  onChange={(e) => handlePaymentDataChange('monto_directo', e.target.value)}
                  placeholder="0.00"
                  disabled={applyingPayment || selectedCargos.length > 0}
                  style={selectedCargos.length > 0 ? {backgroundColor: '#e9ecef'} : {}}
                />
                {selectedCargos.length > 0 && (
                  <small style={{color: '#666', fontSize: '11px'}}>Monto calculado de cargos seleccionados</small>
                )}
              </div>
              <div className={styles.formGroup}>
                <label>Forma de Pago *</label>
                <select
                  value={paymentData.forma_pago}
                  onChange={(e) => handlePaymentDataChange('forma_pago', e.target.value)}
                  disabled={applyingPayment}
                >
                  <option value="">Seleccione forma de pago</option>
                  <option value="EFECTIVO">Efectivo</option>
                  <option value="PAGO_EN_LINEA">Pago en línea</option>
                  <option value="TARJETA_DEBITO">Tarjeta de débito</option>
                  <option value="TARJETA_CREDITO">Tarjeta de crédito</option>
                  <option value="DEPOSITO_BANCARIO">Depósito bancario</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Referencia Bancaria</label>
                <input 
                  type="text"
                  value={paymentData.referencia_bancaria}
                  onChange={(e) => handlePaymentDataChange('referencia_bancaria', e.target.value)}
                  placeholder="Número de referencia"
                  disabled={applyingPayment}
                />
              </div>
              <div className={styles.formGroup}>
                <label>Fecha de Pago *</label>
                <div className={styles.dateInputWrapper}>
                  <input 
                    type="date"
                    value={paymentData.fecha_pago}
                    onChange={(e) => handlePaymentDataChange('fecha_pago', e.target.value)}
                    disabled={applyingPayment}
                  />
                  <svg className={styles.calendarIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="4" width="18" height="18" rx="2" stroke="#898d8d" strokeWidth="2"/>
                    <path d="M16 2V6M8 2V6M3 10H21" stroke="#898d8d" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup} style={{flex: 1}}>
                <label>Observaciones</label>
                <textarea
                  value={paymentData.observaciones}
                  onChange={(e) => handlePaymentDataChange('observaciones', e.target.value)}
                  placeholder="Observaciones del pago..."
                  rows="2"
                  disabled={applyingPayment}
                  style={{width: '100%', resize: 'vertical'}}
                />
              </div>
            </div>
            <div className={styles.paymentActionButtons}>
              <button 
                className={styles.cancelPaymentButton}
                onClick={() => {
                  setSelectedCargos([]);
                  setPaymentData({
                    forma_pago: '',
                    referencia_bancaria: '',
                    fecha_pago: getFechaActual(),
                    observaciones: '',
                    monto_directo: ''
                  });
                }}
                disabled={applyingPayment}
              >
                Limpiar formulario
              </button>
              <button
                className={styles.confirmPaymentButton}
                onClick={handleApply}
                disabled={applyingPayment || loading || !paymentData.forma_pago}
                style={{
                  backgroundColor: applyingPayment || loading || !paymentData.forma_pago ? '#aaa' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 32px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: applyingPayment || loading || !paymentData.forma_pago ? 'not-allowed' : 'pointer',
                  opacity: applyingPayment || loading || !paymentData.forma_pago ? 0.6 : 1,
                }}
              >
                {applyingPayment ? 'PROCESANDO...' : 'REGISTRAR PAGO'}
              </button>
            </div>
          </div>
        </div>

      {/* Sección de Historial de Pagos del Estudiante */}
      <HistorialPagosSection 
        studentId={student.id}
        refreshKey={historialPagosKey}
        student={student}
      />

    </div>
    </ErrorBoundary>
  );
}

export default ChargeForm;
